import { withOktaAuth } from "@okta/okta-react";
import { AuthState } from "@okta/okta-auth-js";
import * as React from "react";
import { connect } from "react-redux";
import { compose, Dispatch } from "redux";
import { Routes } from "../../AppConstants";
import { validateAuthState } from "../../Auth/AuthHelper";
import i18n from "../../i18n";
import { IStoreState } from "../../Model";
import { Error } from "../../Shared/Error";
import { LoadingIndicator } from "../../Shared/LoadingIndicator";
import Responses from "../../Shared/ResponseTable/ResponseList";
import { getResponseListThunk, setSortOrder, resetSortOrder, clearAllFilters } from "../duck/Actions";
import { WithOktaAuthProps } from "src/ts/Auth/duck/Models";
import { IPublishedRequestParams } from "../duck/Operations";

export interface IResponsesStateProps {
  failed: boolean
  fetched: boolean
  fetching: boolean
  responses: Api.IPagedResult<Api.IResponseListViewModel>
  orderBy: string
  filters: any;
  filteredResults: Api.IResponseListViewModel[];
}

interface IResponsesPageDispatchProps {
  fetchResponseList: (
    auth: AuthState | null,
    requestParams: IPublishedRequestParams
  ) => any;
  setSortOrder: (orderBy: string) => void;
  resetSortOrder: () => void;
  clearAllFilters: () => void;
}

interface IResponsesPageOwnProps {
  currentPage: number;
  sortOrder: string;
}

type IResponsesProps = IResponsesStateProps &
  IResponsesPageDispatchProps &
  IResponsesPageOwnProps &
  WithOktaAuthProps;

class ResponseListPage extends React.Component<IResponsesProps> {
  constructor(props: IResponsesProps) {
    super(props);

  }

  private buildRequestParams(): IPublishedRequestParams {
    return {
      pageNumber: this.props.currentPage,
      pageSize: this.props.responses.pageSize,
      skip: (this.props.currentPage - 1) * this.props.responses.pageSize,
      filter: { filters: this.props.filters },
      orderBy: this.props.orderBy
    };
  }

  public componentDidMount() {
    if (this.props.authState?.isAuthenticated) {
      this.props.resetSortOrder();
      this.props.fetchResponseList(
        this.props.authState,
        this.buildRequestParams()
      );
    } else {
      validateAuthState(this.props.authState);
    }
  }

  public componentDidUpdate(prevProps: IResponsesProps) {
    if (this.props.authState.isAuthenticated) {
      // Don't make another API call if one is already in progress
      // This prevents the race condition when page size changes trigger both
      // a direct API call from Pagination and a URL change that triggers componentDidUpdate
      if (this.props.fetching) {
        return;
      }
      if (
        prevProps.currentPage !== this.props.currentPage ||
        prevProps.orderBy !== this.props.orderBy || prevProps.filters !== this.props.filters
      ) {
        this.props.fetchResponseList(
          this.props.authState,
          this.buildRequestParams()
        );
      }
    } else {
      validateAuthState(this.props.authState);
    }
  }

  public componentWillUnmount() {
    // Reset sorting when navigating away from the Updates tab
    this.props.resetSortOrder();
    this.props.clearAllFilters();
  }

  public handleSortChange = (orderBy: string) => {
    this.props.setSortOrder(orderBy);
  }

  // Wrapper function for Pagination component that expects (pageNumber, itemPerPage, auth) signature
  public handlePageSizeChange = (pageNumber: number, itemPerPage: number, auth: AuthState | null) => {
    if (auth) {
      const requestParams: IPublishedRequestParams = {
        pageNumber: pageNumber,
        pageSize: itemPerPage,
        skip: (pageNumber - 1) * itemPerPage,
        filter: { filters: this.props.filters },
        orderBy: this.props.orderBy
      };
      this.props.fetchResponseList(auth, requestParams);
    }
  }
  public render() {
    if (!this.props.authState) {
      return <LoadingIndicator busy={true} />
    }
    if (this.props.authState?.error) {
      throw this.props.authState.error;
    }
    return (
      <React.Fragment>
        <h1 className="title">{i18n.t("responseList.heading")}</h1>
        {this.props.failed && (
          <Error message={i18n.t("responseList.fetchingResponsesFailed")} />
        )}
        <LoadingIndicator busy={this.props.fetching} />
        {this.props.fetched && (
          <Responses
            list={this.props.responses}
            baseRoute={Routes.responseList}
            isDraftList={false}
            handlePageSizeChange={this.handlePageSizeChange}
            authState={this.props.authState}
            onSortChange={this.handleSortChange}
            orderBy={this.props.orderBy}
          />
        )}
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state: IStoreState) => {
  return {
    ...state.responses
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    fetchResponseList: (
      auth: AuthState,
      requestParams: IPublishedRequestParams
    ) => {
      dispatch(
        getResponseListThunk(
          auth,
          requestParams
        )
      );
    },
    setSortOrder: (orderBy: string) => {
      dispatch(setSortOrder(orderBy));
    },
    resetSortOrder: () => {
      dispatch(resetSortOrder());
    },
    clearAllFilters: () => {
      dispatch(clearAllFilters() as any);
    }
  };
};

const hoc = compose(
  withOktaAuth,
  connect<
    IResponsesStateProps,
    IResponsesPageDispatchProps,
    IResponsesPageOwnProps
  >(mapStateToProps, mapDispatchToProps)
)(ResponseListPage);

export { hoc as ResponseListPage };
